import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Spin,
  Alert,
  Typography,
  Space,
  Tag,
  Button,
  Divider,
  Empty,
  Progress,
} from 'antd';
import {
  ToolOutlined,
  FileTextOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  UserOutlined,
} from '@ant-design/icons';
import HallazgoChip from 'components/admin/Hallazgos/HallazgoChip';
import { getOrderSummaryApi } from 'api/hallazgoFlujo';
import { useTrazabilidadDrawer } from 'providers/Trazabilidad/TrazabilidadDrawerProvider';
import { useTenantColors } from 'hooks/useTenantColors';
import DownloadOrderButton from './downloads/DownloadOrderButton';
import { drawerPropsFromLevel, fmtDateTime } from './drawerShared';

// Fase 9: drawer de summary de ODT con cascada visual.

function OrderSummaryDrawer({ open, idOrder, onClose, level = 0 }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const { openHallazgo, openBudget } = useTrazabilidadDrawer();
  const { secondary } = useTenantColors();

  useEffect(() => {
    if (!open || !idOrder) return;
    setLoading(true);
    setError(null);
    setData(null);
    getOrderSummaryApi(idOrder)
      .then((res) => {
        if (res && res.id) setData(res);
        else setError(res?.message || 'No se pudo cargar la ODT');
      })
      .catch((err) => setError(err?.message || 'Error al cargar'))
      .finally(() => setLoading(false));
  }, [open, idOrder]);

  const finishedCount = data?.items?.filter((i) => i.finish).length ?? 0;
  const totalCount = data?.items?.length ?? 0;
  const progressPct = totalCount ? Math.round((finishedCount / totalCount) * 100) : 0;

  return (
    <Drawer
      {...drawerPropsFromLevel(level)}
      open={open}
      onClose={onClose}
      width={620}
      destroyOnHidden
      title={
        <Space align="center" size="middle">
          <ToolOutlined style={{ color: secondary }} />
          <span>ODT {idOrder ? `#${idOrder}` : ''}</span>
          {data?.completed_at ? (
            <Tag icon={<CheckCircleFilled />} color="success">
              Finalizada
            </Tag>
          ) : (
            <Tag icon={<ClockCircleOutlined />} color="processing">
              En curso
            </Tag>
          )}
          {data?.id ? <DownloadOrderButton idOrder={data.id} size="small" /> : null}
        </Space>
      }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={error} />
      ) : data ? (
        <>
          {/* Metadata */}
          {data.budget ? (
            <Button
              block
              icon={<FileTextOutlined />}
              onClick={() => openBudget(data.budget.id)}
              style={{ textAlign: 'left', marginBottom: 10 }}
            >
              <span style={{ marginRight: 8 }}>Presupuesto #{data.budget.id}</span>
              {data.budget.cliente?.name ? (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  <UserOutlined style={{ marginRight: 4 }} />
                  {data.budget.cliente.name}
                </Typography.Text>
              ) : null}
            </Button>
          ) : null}

          <div
            style={{
              padding: 12,
              background: '#0f172a',
              borderRadius: 8,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 8,
              }}
            >
              <Typography.Text
                style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11, letterSpacing: 0.8 }}
              >
                PROGRESO
              </Typography.Text>
              <span
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 16,
                  fontWeight: 600,
                  color: '#fff',
                }}
              >
                {finishedCount} / {totalCount}
              </span>
            </div>
            <Progress
              percent={progressPct}
              strokeColor={secondary}
              trailColor="rgba(255,255,255,0.12)"
              showInfo={false}
              size="small"
            />
          </div>

          {data.assigned_to ? (
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
              <UserOutlined style={{ marginRight: 6 }} />
              Técnico: {data.assigned_to}
            </Typography.Text>
          ) : null}
          {data.completed_at ? (
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              Cerrada el {fmtDateTime(data.completed_at)}
            </Typography.Text>
          ) : null}

          <Divider style={{ margin: '12px 0' }} />

          {/* Tareas */}
          <Typography.Text
            strong
            style={{
              fontSize: 11,
              letterSpacing: 0.8,
              color: '#64748b',
              display: 'block',
              marginBottom: 8,
            }}
          >
            TAREAS · {totalCount}
          </Typography.Text>
          {data.items && data.items.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.items.map((it) => (
                <div
                  key={it.id}
                  style={{
                    padding: 10,
                    background: it.finish ? '#e4f0e7' : '#f6f7f9',
                    borderLeft: `3px solid ${it.finish ? '#2e6b3a' : '#cbd2d9'}`,
                    borderRadius: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
                      <div
                        style={{
                          color: '#1e293b',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {it.description || '—'}
                      </div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>
                        {it.elevator || '—'}
                        {it.cantidad_real != null ? ` · consumido ${it.cantidad_real}` : ''}
                      </div>
                    </div>
                    {it.status ? (
                      <Tag color={it.status.color ?? 'default'}>{it.status.name}</Tag>
                    ) : null}
                  </div>
                  {it.hallazgo ? (
                    <HallazgoChip
                      hallazgo={it.hallazgo}
                      compact
                      onClick={() => openHallazgo(it.hallazgo.id)}
                    />
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <Empty description="Sin tareas" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}

          {/* Firmas S3 */}
          {(data.recepcionist_signature || data.specialist_signature) && (
            <div style={{ marginTop: 16 }}>
              <Typography.Text
                strong
                style={{ fontSize: 11, letterSpacing: 0.8, color: '#64748b' }}
              >
                FIRMAS REGISTRADAS
              </Typography.Text>
              <Typography.Paragraph
                type="secondary"
                style={{ fontSize: 12, marginTop: 4, fontFamily: "'IBM Plex Mono', monospace" }}
              >
                {data.specialist_signature ? `Especialista: ${data.specialist_signature}` : ''}
                {data.specialist_signature && data.recepcionist_signature ? <br /> : null}
                {data.recepcionist_signature
                  ? `Recepcionista (${data.recepcionist_name || '—'}): ${
                      data.recepcionist_signature
                    }`
                  : ''}
              </Typography.Paragraph>
            </div>
          )}
        </>
      ) : null}
    </Drawer>
  );
}

export default OrderSummaryDrawer;
